import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { operatorApi, type Tenant } from '../../api/operator'
import { useAdminToken } from './AdminLayout'

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function daysLeft(iso: string | null): number | null {
  if (!iso) return null
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
}

function expiryColor(days: number | null) {
  if (days === null) return ''
  if (days < 0)  return 'text-red-600'
  if (days < 30) return 'text-amber-600'
  return 'text-emerald-600'
}

function StatCard({ label, value, sub, accent }: { label: string; value: number; sub?: string; accent?: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-3xl font-bold ${accent ?? 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  )
}

export default function AdminOverview() {
  const token   = useAdminToken()
  const navigate = useNavigate()

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['op-tenants', token],
    queryFn: () => operatorApi.listTenants(token).then((r) => r.data),
    staleTime: 60_000,
  })

  const total    = tenants.length
  const active   = tenants.filter((t) => t.active && (daysLeft(t.license_expires_at) ?? 1) >= 0).length
  const inactive = tenants.filter((t) => !t.active).length
  const expiring = tenants.filter((t) => {
    const d = daysLeft(t.license_expires_at)
    return d !== null && d >= 0 && d <= 30
  }).length

  const expiringSoon: Tenant[] = tenants
    .filter((t) => {
      const d = daysLeft(t.license_expires_at)
      return d !== null && d <= 90
    })
    .sort((a, b) =>
      new Date(a.license_expires_at!).getTime() - new Date(b.license_expires_at!).getTime()
    )

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Pregled</h1>
        <p className="text-sm text-gray-500 mt-0.5">Status svih tenanta u sistemu</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-7 h-7 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Ukupno" value={total} />
            <StatCard label="Aktivni" value={active} accent="text-emerald-600" />
            <StatCard label="Neaktivni" value={inactive} accent={inactive > 0 ? 'text-red-600' : 'text-gray-900'} />
            <StatCard
              label="Ističe ≤30 dana"
              value={expiring}
              accent={expiring > 0 ? 'text-amber-600' : 'text-gray-900'}
              sub={expiring > 0 ? 'Potrebno produžiti' : undefined}
            />
          </div>

          {/* Expiring soon */}
          {expiringSoon.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">Ističu uskoro (90 dana)</h2>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Naziv</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Opština</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ističe</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Dana</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {expiringSoon.map((t) => {
                    const d = daysLeft(t.license_expires_at)
                    return (
                      <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-6 py-3 font-medium text-gray-900">{t.name}</td>
                        <td className="px-6 py-3 text-gray-500">{t.municipality}</td>
                        <td className="px-6 py-3 text-gray-500">{fmtDate(t.license_expires_at)}</td>
                        <td className={`px-6 py-3 font-semibold ${expiryColor(d)}`}>
                          {d !== null ? (d < 0 ? 'Istekla' : `${d}d`) : '—'}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button
                            onClick={() => navigate(`/admin/tenants/${t.id}`)}
                            className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight"
                          >
                            Upravljati →
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {expiringSoon.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <p className="text-sm text-gray-500">Nema tenanta kojima licenca ističe u narednih 90 dana.</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
